import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '@/hooks/useAuth';
import { getAssetPath, getGameVersion } from '@utils/brandHelper';
import { useBackgroundRotation } from '@brandscripts/useBackgroundRotation';
import LoadingBar from '@/components/ui/general/loader/LoadingBar';
import AudioManager from '@/audio/AudioManager';
import FloatingControls from '@components/ui/general/FloatingControls/FloatingControls';
import MusicWidget from '@components/ui/general/MusicWidget/MusicWidget';
import LanguageSelector from '@components/ui/general/LanguageSelector/LanguageSelector';
import { useT } from '@/i18n';
import './Register.css';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const t = useT();

  // Get random background image for Alberath
  const backgroundImage = useBackgroundRotation();

  const { register, isRegistering, registerError, isLoadingMetadata } = useAuth();

  // Initialize AudioManager and play music (continues between Login/Register)
  useEffect(() => {
    AudioManager.init();
    AudioManager.playMusicIfSilent('maintheme');
    // No cleanup - music continues when navigating to Login
  }, []);

  // Unlock audio on first user interaction (browser policy)
  useEffect(() => {
    const unlock = () => {
      AudioManager.unlock();
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);

    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  // Play error sound when registration fails
  useEffect(() => {
    if (registerError) {
      AudioManager.playSfx('error');
    }
  }, [registerError]);

  const handleRegister = (e) => {
    e.preventDefault();
    register({
      name,
      email,
      password,
      password_confirmation: passwordConfirmation,
      terms_accepted: termsAccepted
    });
  };

  // Show loading bar while metadata is loading
  if (isLoadingMetadata) {
    return <LoadingBar targetProgress={100} message={t('auth.loading_game')} />;
  }

  return (
  <>
    <div
      className="register-container"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <img src={getAssetPath('logo.webp')} alt="Logo" className="register-logo" />

      <h1 className="auth-title">{t('auth.create_account')}</h1>

      <form onSubmit={handleRegister} className="register-form">
        <input
          type="text"
          name="name"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('auth.name')}
          required
          data-sfx-hover="input"
        />
        <input
          type="email"
          name="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.email')}
          required
          data-sfx-hover="input"
        />
        <input
          type="password"
          name="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('auth.password')}
          required
          data-sfx-hover="input"
        />
        <input
          type="password"
          name="password_confirmation"
          autoComplete="new-password"
          value={passwordConfirmation}
          onChange={(e) => setPasswordConfirmation(e.target.value)}
          placeholder={t('auth.confirm_password')}
          required
          data-sfx-hover="input"
        />

        <label className="register-terms">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            required
            data-sfx="button"
          />
          <span>{t('auth.terms_accept')}</span>
        </label>

        {registerError && (
          <p className="error-message">
            {registerError.userMessage || t('auth.registration_failed')}
          </p>
        )}

        <p className="beta-warning">
          {t('auth.beta_warning')}
        </p>

        <button className="button button-cta button-cta-arrow" type="submit" disabled={isRegistering} data-sfx="button">
          <span>{isRegistering ? t('auth.creating_account') : t('auth.create_account')}</span>
          <span className="arrow-svg">
            <svg width="66px" height="43px" viewBox="0 0 66 43" version="1.1" xmlns="http://www.w3.org/2000/svg">
              <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                <path className="arrow-one" d="M40.1543933,3.89485454 L43.9763149,0.139296592 C44.1708311,-0.0518420739 44.4826329,-0.0518571125 44.6771675,0.139262789 L65.6916134,20.7848311 C66.0855801,21.1718824 66.0911863,21.8050225 65.704135,22.1989893 C65.7000188,22.2031791 65.6958657,22.2073326 65.6916762,22.2114492 L44.677098,42.8607841 C44.4825957,43.0519059 44.1708242,43.0519358 43.9762853,42.8608513 L40.1545186,39.1069479 C39.9575152,38.9134427 39.9546793,38.5968729 40.1481845,38.3998695 C40.1502893,38.3977268 40.1524132,38.395603 40.1545562,38.3934985 L56.9937789,21.8567812 C57.1908028,21.6632968 57.193672,21.3467273 57.0001876,21.1497035 C56.9980647,21.1475418 56.9959223,21.1453995 56.9937605,21.1432767 L40.1545208,4.60825197 C39.9574869,4.41477773 39.9546013,4.09820839 40.1480756,3.90117456 C40.1501626,3.89904911 40.1522686,3.89694235 40.1543933,3.89485454 Z" fill="#FFFFFF"></path>
                <path className="arrow-two" d="M20.1543933,3.89485454 L23.9763149,0.139296592 C24.1708311,-0.0518420739 24.4826329,-0.0518571125 24.6771675,0.139262789 L45.6916134,20.7848311 C46.0855801,21.1718824 46.0911863,21.8050225 45.704135,22.1989893 C45.7000188,22.2031791 45.6958657,22.2073326 45.6916762,22.2114492 L24.677098,42.8607841 C24.4825957,43.0519059 24.1708242,43.0519358 23.9762853,42.8608513 L20.1545186,39.1069479 C19.9575152,38.9134427 19.9546793,38.5968729 20.1481845,38.3998695 C20.1502893,38.3977268 20.1524132,38.395603 20.1545562,38.3934985 L36.9937789,21.8567812 C37.1908028,21.6632968 37.193672,21.3467273 37.0001876,21.1497035 C36.9980647,21.1475418 36.9959223,21.1453995 36.9937605,21.1432767 L20.1545208,4.60825197 C19.9574869,4.41477773 19.9546013,4.09820839 20.1480756,3.90117456 C20.1501626,3.89904911 20.1522686,3.89694235 20.1543933,3.89485454 Z" fill="#FFFFFF"></path>
                <path className="arrow-three" d="M0.154393339,3.89485454 L3.97631488,0.139296592 C4.17083111,-0.0518420739 4.48263286,-0.0518571125 4.67716753,0.139262789 L25.6916134,20.7848311 C26.0855801,21.1718824 26.0911863,21.8050225 25.704135,22.1989893 C25.7000188,22.2031791 25.6958657,22.2073326 25.6916762,22.2114492 L4.67709797,42.8607841 C4.48259567,43.0519059 4.17082418,43.0519358 3.97628526,42.8608513 L0.154518591,39.1069479 C-0.0424848215,38.9134427 -0.0453206733,38.5968729 0.148184538,38.3998695 C0.150289256,38.3977268 0.152413239,38.395603 0.154556228,38.3934985 L16.9937789,21.8567812 C17.1908028,21.6632968 17.193672,21.3467273 17.0001876,21.1497035 C16.9980647,21.1475418 16.9959223,21.1453995 16.9937605,21.1432767 L0.15452076,4.60825197 C-0.0425130651,4.41477773 -0.0453986756,4.09820839 0.148075568,3.90117456 C0.150162624,3.89904911 0.152268631,3.89694235 0.154393339,3.89485454 Z" fill="#FFFFFF"></path>
              </g>
            </svg>
          </span>
        </button>

        <p className="register-login-link">
          {t('auth.have_account')} <Link to="/">{t('common.login')}</Link>
        </p>
      </form>
    </div>

    <FloatingControls position="top-left">
      <MusicWidget />
      <LanguageSelector />
    </FloatingControls>
    <span className="version-badge">{getGameVersion()}</span>
  </>
  );
}
